/**
 * Busca da tela de Assinaturas: POR QUE cada assinatura apareceu.
 *
 * A busca procura em nome, razão social, nome fantasia, CNPJ, código e número
 * do contrato, e TAMBÉM nas observações. As observações entram de propósito: é
 * onde ficam os blocos "AUDITORIA DE REAJUSTE", e digitar "auditoria" lista os
 * contratos sinalizados. ⛔ Não tirar `notes` da busca.
 *
 * O problema era misturar sem explicar. Uma observação que cita OUTRO cliente
 * contaminava a busca por nome: em 10/2026, buscar "conceito brasil" devolvia
 * as quatro assinaturas do grupo PARANAFRIGOR, porque as observações delas
 * diziam "igualando a regra ao grupo Conceito Brasil". Agora o resultado vem
 * em dois blocos: o que casou por identidade primeiro, e o que casou SÓ pelas
 * observações depois, rotulado e com o trecho que casou.
 */

export type MotivoDaBusca = 'identidade' | 'observacoes';

const norm = (v: any) => String(v || '').toLowerCase();

export function casaPorIdentidade(c: any, cl: any, termo: string): boolean {
  const s = norm(termo).trim();
  if (!s) return true;
  const digitos = s.replace(/\D/g, '');
  return (
    norm(cl?.name).includes(s) ||
    norm(cl?.tradeName).includes(s) ||
    norm(cl?.companyName).includes(s) ||
    norm(c?.code).includes(s) ||
    norm(c?.contractNumber).includes(s) ||
    (digitos.length >= 3 && String(cl?.cnpj || '').replace(/\D/g, '').includes(digitos))
  );
}

export function casaPorObservacao(c: any, termo: string): boolean {
  const s = norm(termo).trim();
  return !!s && norm(c?.notes).includes(s);
}

/** null = não casa; senão, por qual caminho (identidade tem precedência). */
export function motivoDaBusca(c: any, cl: any, termo: string): MotivoDaBusca | null {
  if (casaPorIdentidade(c, cl, termo)) return 'identidade';
  if (casaPorObservacao(c, termo)) return 'observacoes';
  return null;
}

/** "…igualando a regra ao grupo Conceito Brasil…": o pedaço da observação que casou. */
export function trechoDaObservacao(notes: string | null | undefined, termo: string, raio = 40): string | null {
  const texto = String(notes || '').replace(/\s+/g, ' ').trim();
  const s = norm(termo).trim();
  if (!texto || !s) return null;
  const i = texto.toLowerCase().indexOf(s);
  if (i < 0) return null;
  const ini = Math.max(0, i - raio);
  const fim = Math.min(texto.length, i + s.length + raio);
  return `${ini > 0 ? '…' : ''}${texto.slice(ini, fim)}${fim < texto.length ? '…' : ''}`;
}

/** Partição estável: identidade primeiro, observações depois, cada lado na ordem que veio. */
export function separarPorMotivo<T>(lista: T[], soObservacao: (x: T) => boolean): T[] {
  return [...lista.filter((x) => !soObservacao(x)), ...lista.filter(soObservacao)];
}
